import type { SaveGame } from '../types';
import { supabase } from '../lib/supabase';

export type GameMode = SaveGame['gameMode'];

export interface GameSaveSlot {
  id: string;
  slot: number;
  title: string;
  gameMode: GameMode;
  clubName: string;
  clubCrestId?: string;
  saveData: SaveGame;
  createdAt: string;
  updatedAt: string;
}

type GameSaveRow = {
  id: string;
  slot: number;
  title: string;
  game_mode: GameMode;
  club_name: string;
  club_crest_id: string | null;
  save_data: SaveGame;
  created_at: string;
  updated_at: string;
};

const slotColumns = 'id, slot, title, game_mode, club_name, club_crest_id, save_data, created_at, updated_at';

function client() {
  if (!supabase) throw new Error('Supabase no está configurado.');
  return supabase;
}

function asSlot(row: GameSaveRow): GameSaveSlot {
  return {
    id: row.id,
    slot: row.slot,
    title: row.title,
    gameMode: row.game_mode,
    clubName: row.club_name,
    clubCrestId: row.club_crest_id ?? undefined,
    saveData: row.save_data,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function listGameSaves() {
  const { data, error } = await client().from('game_saves').select(slotColumns).order('slot');
  if (error) throw new Error('No se han podido cargar las partidas.');
  return (data as GameSaveRow[]).map(asSlot);
}

export async function createGameSave(slot: number, title: string, saveData: SaveGame) {
  const { data, error } = await client().from('game_saves').insert({
    slot,
    title: title.trim().slice(0, 48) || saveData.clubName,
    game_mode: saveData.gameMode,
    club_name: saveData.clubName,
    club_crest_id: saveData.clubCrestId ?? null,
    save_data: saveData,
  }).select(slotColumns).single();
  if (error || !data) throw new Error(error?.code === '23505' ? 'Esa ranura ya está ocupada.' : 'No se ha podido crear la partida.');
  return asSlot(data as GameSaveRow);
}

export async function persistGameSave(id: string, saveData: SaveGame) {
  const { error } = await client().from('game_saves').update({
    title: saveData.clubName,
    game_mode: saveData.gameMode,
    club_name: saveData.clubName,
    club_crest_id: saveData.clubCrestId ?? null,
    save_data: saveData,
  }).eq('id', id);
  if (error) throw new Error('No se ha podido guardar la partida en la nube.');
}

export async function removeGameSave(id: string) {
  const { error } = await client().from('game_saves').delete().eq('id', id);
  if (error) throw new Error('No se ha podido eliminar la partida.');
}
